import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import ConnectedAppsApi from '../../lib/api/connectedApps'
import { appKind, describeWhen, initial, scopeLines } from '../../lib/connectedApps'
import './index.scss'

/**
 * The apps connected to the user's BaseMap account (WEB-7107): AI assistants such as Claude and ChatGPT,
 * partner apps, and BaseMap's own developer portal, each with what it can do and a Disconnect button.
 *
 * Shared by the Settings panel's Connected Apps tab and the standalone /connected-apps page, which the
 * consent page, the AI-assistants help page and the mobile apps link to.
 */
const ConnectedAppsList = ({ token, compact }) => {
  const [apps, setApps] = useState(null)
  const [error, setError] = useState('')
  const [confirming, setConfirming] = useState(null)
  const [working, setWorking] = useState(null)
  const [notice, setNotice] = useState('')
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    setError('')
    ConnectedAppsApi.list({ token })
      .then(list => { if (!cancelled) setApps(list) })
      .catch(err => { if (!cancelled) setError(err.message) })
    return () => { cancelled = true }
  }, [token, attempt])

  const disconnect = app => {
    setWorking(app.clientId)
    setNotice('')
    ConnectedAppsApi.revoke({ clientId: app.clientId, token })
      .then(() => {
        setApps(list => list.filter(a => a.clientId !== app.clientId))
        setNotice(`${app.name} is disconnected. If it's open right now it can finish what it's doing for up to an hour, then it will ask you to sign in again.`)
      })
      .catch(err => {
        // 404: it was already disconnected, e.g. from another tab.
        if (err.status === 404) setApps(list => list.filter(a => a.clientId !== app.clientId))
        else setError(err.message)
      })
      .finally(() => {
        setWorking(null)
        setConfirming(null)
      })
  }

  if (error && !apps)
    return (
      <div className={`connected-apps${compact ? ' connected-apps--compact' : ''}`}>
        <p className='connected-apps-error'>{error}</p>
        <button className='connected-apps-secondary' onClick={() => setAttempt(n => n + 1)}>Try again</button>
      </div>
    )

  if (!apps) return <div className={`connected-apps${compact ? ' connected-apps--compact' : ''}`}><p className='connected-apps-note'>Loading…</p></div>

  return (
    <div className={`connected-apps${compact ? ' connected-apps--compact' : ''}`}>
      {notice && <p className='connected-apps-notice' role='status'>{notice}</p>}
      {error && <p className='connected-apps-error' role='alert'>{error}</p>}

      {apps.length === 0 ? (
        <div className='connected-apps-empty'>
          <p className='connected-apps-empty-title'>No connected apps</p>
          <p className='connected-apps-note'>
            When you connect BaseMap to an AI assistant such as Claude or ChatGPT, or sign in to another app with
            your BaseMap account, it shows up here. You can see what each one can do, and disconnect it at any time.
          </p>
        </div>
      ) : (
        <ul className='connected-apps-list'>
          {apps.map(app => {
            const used = describeWhen(app.lastUsed)
            const connected = describeWhen(app.firstConnected)
            return (
              <li key={app.clientId} className='connected-app'>
                <div className='connected-app-head'>
                  <span className='connected-app-tile' aria-hidden='true'>{initial(app.name)}</span>
                  <div>
                    <div className='connected-app-name'>{app.name}</div>
                    <div className='connected-app-meta'>
                      {appKind(app)}
                      {connected && ` · Connected ${connected}`}
                      {used && ` · Last used ${used}`}
                    </div>
                  </div>
                </div>

                {scopeLines(app).length > 0 && (
                  <>
                    <p className='connected-app-can'>It can:</p>
                    <ul className='connected-app-scopes'>
                      {scopeLines(app).map(line => <li key={line}>{line}</li>)}
                    </ul>
                  </>
                )}

                {confirming === app.clientId ? (
                  <div className='connected-app-confirm'>
                    <p>{`Disconnect ${app.name}? It won't be able to use your BaseMap account until you connect it again. Anything it saved to your map stays.`}</p>
                    <div className='connected-app-actions'>
                      <button className='connected-apps-danger' disabled={working === app.clientId} onClick={() => disconnect(app)}>
                        {working === app.clientId ? 'Disconnecting…' : 'Disconnect'}
                      </button>
                      <button className='connected-apps-secondary' disabled={working === app.clientId} onClick={() => setConfirming(null)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className='connected-app-actions'>
                    <button className='connected-apps-secondary' onClick={() => setConfirming(app.clientId)}>Disconnect</button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

const mapStateToProps = state => ({
  token: state.auth.token
})

export default connect(mapStateToProps)(ConnectedAppsList)
